import axios from 'axios';
import { API_BASE_URL, API_BASE_URL_FALLBACK } from '../config/backendConfig';

// Create axios instance for airport API - recreate on each import to pick up URL changes
const createAirportApi = () => axios.create({
  baseURL: `${API_BASE_URL}/airports`,
  timeout: 10000,
});

// Fallback API instance using production URL
const createFallbackApi = () => axios.create({
  baseURL: `${API_BASE_URL_FALLBACK}/airports`,
  timeout: 10000,
});

// Get fresh API instance
const getAirportApi = () => createAirportApi();
const getFallbackApi = () => createFallbackApi();

/**
 * Find nearest airports to given coordinates
 * @param {number} latitude - Latitude coordinate
 * @param {number} longitude - Longitude coordinate
 * @param {number} limit - Maximum number of results (default: 5)
 * @param {number} radius - Maximum radius in km (default: 100)
 * @returns {Promise<Object>} Nearest airports data
 */
export const findNearestAirports = async (latitude, longitude, limit = 5, radius = 100) => {
  try {
    const response = await getAirportApi().get('/nearest', {
      params: {
        lat: latitude,
        lon: longitude,
        limit: limit,
        radius: radius
      }
    });

    return {
      success: true,
      data: response.data.data,
      searchLocation: response.data.searchLocation,
      totalFound: response.data.totalFound
    };
  } catch (error) {
    console.error('Error finding nearest airports:', error);
    return {
      success: false,
      error: error.response?.data?.error || 'Failed to find nearest airports',
      data: []
    };
  }
};

/**
 * Search airports by name, city, or IATA code
 * @param {string} query - Search query
 * @param {number} limit - Maximum number of results (default: 10)
 * @returns {Promise<Object>} Search results
 */
export const searchAirports = async (query, limit = 10) => {
  const trySearch = async (api) => {
    const response = await api.get('/search', {
      params: {
        q: query,
        limit: limit
      }
    });
    return response;
  };

  try {
    // Try primary API first
    const response = await trySearch(getAirportApi());
    return {
      success: true,
      data: response.data.data,
      query: response.data.query,
      totalFound: response.data.totalFound
    };
  } catch (primaryError) {
    console.warn('Primary airport search failed, trying fallback:', primaryError.message);
    
    // Try fallback API
    try {
      const fallbackResponse = await trySearch(getFallbackApi());
      return {
        success: true,
        data: fallbackResponse.data.data,
        query: fallbackResponse.data.query,
        totalFound: fallbackResponse.data.totalFound,
        usedFallback: true
      };
    } catch (fallbackError) {
      console.error('Both airport search attempts failed:', {
        primary: primaryError.message,
        fallback: fallbackError.message
      });
      return {
        success: false,
        error: fallbackError.response?.data?.error || 'Failed to search airports',
        data: []
      };
    }
  }
};

/**
 * Get airport details by IATA code
 * @param {string} iataCode - 3-letter IATA code
 * @returns {Promise<Object>} Airport details
 */
export const getAirportDetails = async (iataCode) => {
  try {
    const response = await getAirportApi().get(`/details/${iataCode}`);

    return {
      success: true,
      data: response.data.data
    };
  } catch (error) {
    console.error('Error getting airport details:', error);
    return {
      success: false,
      error: error.response?.data?.error || 'Failed to get airport details',
      data: null
    };
  }
};

/**
 * Get user's current location using browser geolocation API
 * @returns {Promise<Object>} User's coordinates
 */
export const getUserLocation = () => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported by this browser'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy
        });
      },
      (error) => {
        let errorMessage = 'Failed to get location';
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location access denied by user';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location information unavailable';
            break;
          case error.TIMEOUT:
            errorMessage = 'Location request timed out';
            break;
        }
        reject(new Error(errorMessage));
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000 // 5 minutes
      }
    );
  });
};

/**
 * Find nearest airports to user's current location
 * @param {number} limit - Maximum number of results (default: 5)
 * @param {number} radius - Maximum radius in km (default: 100)
 * @returns {Promise<Object>} Nearest airports to user's location
 */
export const findNearestAirportsToUser = async (limit = 5, radius = 100) => {
  try {
    const location = await getUserLocation();
    return await findNearestAirports(location.latitude, location.longitude, limit, radius);
  } catch (error) {
    console.error('Error getting user location:', error);
    return {
      success: false,
      error: error.message,
      data: []
    };
  }
};

/**
 * Enhanced airport search that combines multiple search methods
 * @param {string} query - Search query
 * @param {Object} options - Search options
 * @returns {Promise<Object>} Combined search results
 */
export const enhancedAirportSearch = async (query, options = {}) => {
  const {
    limit = 10,
    includeNearest = false,
    userLocation = null
  } = options;

  try {
    // First, try direct search
    const searchResults = await searchAirports(query, limit);
    
    let results = searchResults.data || [];
    
    // If includeNearest is true and we have user location, add nearest airports
    if (includeNearest && userLocation) {
      const nearestResults = await findNearestAirports(
        userLocation.latitude, 
        userLocation.longitude, 
        Math.floor(limit / 2), 
        50 // 50km radius for nearest
      );
      
      if (nearestResults.success) {
        // Add distance info to nearest airports
        const nearestWithDistance = nearestResults.data.map(airport => ({
          ...airport,
          isNearest: true,
          distanceFromUser: airport.distanceKm
        }));
        
        // Combine results, avoiding duplicates
        const existingIataCodes = new Set(results.map(r => r.iataCode));
        const uniqueNearest = nearestWithDistance.filter(a => !existingIataCodes.has(a.iataCode));
        
        results = [...results, ...uniqueNearest];
      }
    }
    
    // Sort results: exact matches first, then by relevance
    results.sort((a, b) => {
      // Exact IATA code matches first
      if (a.iataCode?.toLowerCase() === query.toLowerCase()) return -1;
      if (b.iataCode?.toLowerCase() === query.toLowerCase()) return 1;
      
      // Then by name relevance
      const aNameMatch = a.name?.toLowerCase().includes(query.toLowerCase());
      const bNameMatch = b.name?.toLowerCase().includes(query.toLowerCase());
      if (aNameMatch && !bNameMatch) return -1;
      if (!aNameMatch && bNameMatch) return 1;
      
      // Then by distance (if available)
      if (a.distanceKm && b.distanceKm) {
        return a.distanceKm - b.distanceKm;
      }
      
      return 0;
    });
    
    return {
      success: true,
      data: results.slice(0, limit),
      query: query,
      totalFound: results.length,
      searchMethod: includeNearest ? 'enhanced' : 'direct'
    };
    
  } catch (error) {
    console.error('Error in enhanced airport search:', error);
    return {
      success: false,
      error: error.message,
      data: []
    };
  }
};

// Default export
const AirportApi = {
  findNearestAirports,
  searchAirports,
  getAirportDetails,
  getUserLocation,
  findNearestAirportsToUser,
  enhancedAirportSearch
};

export default AirportApi;




